import { FC, useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, AlertTriangle, Clock, ChevronDown, Plus } from 'lucide-react';
import clsx from 'clsx';
import { NsKey } from 'enums/NsKey';
import { useNote } from '../contexts/NoteContext';
import { useNotePriorities } from '../hooks/useNotePriorities';
import { DateSort } from '../types/interface';

// Dropdown Chip Component
interface DropdownChipProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

const DropdownChip: FC<DropdownChipProps> = ({
  icon,
  label,
  value,
  options,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel =
    value === 'all' ? label : selectedOption?.label;

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center gap-1.5 h-9 px-3 rounded-full border bg-white transition-colors',
          isOpen ? 'border-slate-300' : 'border-slate-200 hover:bg-slate-50'
        )}
      >
        <span className="text-slate-400">{icon}</span>
        <span className="text-[13px] text-slate-500">{displayLabel}</span>
        <ChevronDown
          size={14}
          className={clsx(
            'text-slate-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 py-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-[140px]">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={clsx(
                'w-full text-left px-3 py-2 text-[13px] transition-colors',
                option.value === value
                  ? 'bg-primary/5 text-primary font-medium'
                  : 'text-slate-600 hover:bg-slate-50'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
interface NoteActionBarProps {
  title?: string;
}

const NoteActionBar: FC<NoteActionBarProps> = ({ title }) => {
  const { t } = useTranslation(NsKey.Note);
  const {
    filters,
    setSearchFilter,
    setPriorityFilter,
    setDateSortFilter,
    setAddingMode
  } = useNote();

  // Get priorities from referential store
  const { priorityFilterOptions } = useNotePriorities();

  // Merge with translated "all" label
  const priorityOptions = useMemo(
    () => [
      { value: 'all', label: t('FILTERS.PRIORITY.ALL') },
      ...priorityFilterOptions.filter((opt) => opt.value !== 'all')
    ],
    [t, priorityFilterOptions]
  );

  const dateSortOptions = useMemo(
    () => [
      { value: 'week', label: t('FILTERS.DATE.WEEK') },
      { value: 'month', label: t('FILTERS.DATE.MONTH') }
    ],
    [t]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(e.target.value);
  };

  const handlePriorityChange = (value: string) => {
    setPriorityFilter(value);
  };

  const handleDateSortChange = (value: string) => {
    setDateSortFilter(value as DateSort);
  };

  const handleAddClick = () => {
    setAddingMode(true);
  };

  return (
    <div className="px-4 pt-4 pb-3">
      {/* Title with Add button */}
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-xl font-semibold text-slate-800">{title ?? t('TITLE')}</h2>
        <button
          onClick={handleAddClick}
          className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Search input */}
        <div className="relative w-fit">
          <input
            type="text"
            value={filters.search}
            onChange={handleSearchChange}
            placeholder={t('SEARCH_PLACEHOLDER')}
            size={22}
            className="h-9 pl-3 pr-9 rounded-full border border-slate-200 bg-white text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-slate-300 transition-colors min-w-[180px]"
          />
          <Search
            size={15}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
        </div>

        {/* Priority dropdown */}
        <DropdownChip
          icon={<AlertTriangle size={14} />}
          label={t('FILTERS.PRIORITY.LABEL')}
          value={filters.priorityCode}
          options={priorityOptions}
          onChange={handlePriorityChange}
        />

        {/* Date dropdown */}
        <DropdownChip
          icon={<Clock size={14} />}
          label={t('FILTERS.DATE.LABEL')}
          value={filters.dateSort}
          options={dateSortOptions}
          onChange={handleDateSortChange}
        />

        {/* More filters link */}
        <button className="text-[13px] text-slate-400 hover:text-slate-600 whitespace-nowrap transition-colors">
          {t('MORE_FILTERS')}
        </button>
      </div>
    </div>
  );
};

export default NoteActionBar;
